<script setup lang="ts">
import HasPermission from "@/components/HasPermission.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import RoundedBox from "@/components/RoundedBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import AddressAutofillForm, {
  AddressForm,
} from "@/components/address/AddressAutofillForm.vue";
import useAddress from "@/composables/useAddress";
import useCompanyAddress from "@/modules/companies/composables/useCompanyAddress";
import { CompanyEditTab } from "@/modules/companies/interfaces/companyEditTab";
import { useQueryClient } from "@tanstack/vue-query";
import { inject, ref } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";

interface Props extends CompanyEditTab {}

const props = defineProps<Props>();
const route = useRoute();
const queryClient = useQueryClient();
const { data, isLoading, createMutation, refetch } = useCompanyAddress(
  Number(route.params.id),
);

const toast = useToast();
const addressAutofill = ref();
const { updateAddressAsync, updateAddressLoading, createAddressMutation } =
  useAddress({
    params: { id: data.value?.id! },
    immediate: false,
  });
const { invalidateQueryData } = inject("editCompanyProvides") as {
  invalidateQueryData: () => Promise<void>;
};

const handleCreateAddress = async () => {
  try {
    if (!addressAutofill.value) return;

    const addressForm = addressAutofill.value
      .addressForm as Required<AddressForm>;

    await createMutation.mutateAsync({
      companyId: Number(route.params.id),
      ...addressForm,
    });

    toast.success("Dirección creada correctamente");
  } catch (err: any) {
    toast.error(err.response.data.message);
  } finally {
    await refetch();
  }
};

const handleSubmit = async () => {
  try {
    if (!addressAutofill.value) return;

    const addressForm = addressAutofill.value
      .addressForm as Required<AddressForm>;

    await updateAddressAsync({
      params: { ...addressForm, id: data.value?.id! },
      config: { raiseToast: true },
    });
  } catch (err) {
    console.log(err);
  } finally {
    await refetch();
  }
};
</script>

<template>
  <RoundedBox class="bg-white">
    <section class="w-full grid gap-[1.563rem]">
      <h2 class="text-xl font-medium">Dirección</h2>

      <AddressAutofillForm
        ref="addressAutofill"
        :default-address="data"
        v-if="!isLoading"
      />

      <HasPermission
        name-permission="update-address"
        :entity-type="'App\\Models\\Company'"
      >
        <StyledButton
          v-if="data"
          label="Guardar"
          class="bg-[#253C70] text-white ml-auto"
          @click="handleSubmit()"
          :loading="updateAddressLoading"
          :disabled="updateAddressLoading"
        />
      </HasPermission>

      <HasPermission
        name-permission="create-address"
        :entity-type="'App\\Models\\Company'"
      >
        <StyledButton
          v-if="!data"
          label="Crear"
          class="bg-[#253C70] text-white ml-auto"
          @click="handleCreateAddress()"
          :loading="createMutation.isLoading.value"
          :disabled="createMutation.isLoading.value"
        />
      </HasPermission>
    </section>

    <LoadingModal :when="isLoading" />
  </RoundedBox>
</template>

<style scoped></style>
